// Picker de fondos para el modo "Imagen con modelo" de Interfaz V2.
//
// Espejea a ModelPicker en estructura: 3 sub-pantallas controladas por
// un mini state machine (`view`):
//
//   1. 'list'     — carrusel con tile "Automático" sticky + fondos de
//                   biblioteca + fondos guardados del user. Debajo dos
//                   botones: "Describí el fondo" + "Cargá tu propio fondo".
//   2. 'describe' — vista con un textarea estilo chat input + botón
//                   "Mejorar" (no-op por ahora) + Confirmar.
//   3. 'upload'   — file picker + preview + Confirmar (sube a Supabase
//                   y queda en la biblioteca del user para reuso).
//
// Variants: Sheet (mobile bottom sheet) e Inline (desktop, embedded en
// el ConfigurePanel).

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ArrowLeft,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Type as TypeIcon,
  Upload,
  UserPlus,
  X,
} from 'lucide-react';
import {
  uploadFondo,
  type FondoBiblioteca,
  type FondoGuardado,
} from './fondosApi';
import { AnimatedBlurBg } from './AnimatedBlurBg';
import { HoverScrollCarousel } from './HoverScrollCarousel';
import { AddOptionCard } from './components/AddOptionCard';
import { normalizeImageForUpload } from '../../utils/heicConvert';
import CachedImg from '../../components/CachedImg';
import { useExitTransition } from '../../hooks/useExitTransition';
import {
  useDescribeChat,
  type DescribeChatMessage,
} from './useDescribeChat';

// ─── Tipos ─────────────────────────────────────────────────────────────

export type FondoSelection =
  | { mode: 'auto' }
  | { mode: 'blanco' }
  | {
      mode: 'biblioteca';
      id: number;
      name: string;
      imageUrl: string;
    }
  | {
      mode: 'guardado';
      fileName: string;
      name: string;
      imageUrl: string;
    }
  | { mode: 'descripcion'; text: string };

export type FondoPickerView = 'list' | 'describe' | 'upload' | 'all';

export const fondoSelectionLabel = (sel: FondoSelection | null): string => {
  if (!sel) return 'Sugerido';
  if (sel.mode === 'auto') return 'Automático';
  if (sel.mode === 'blanco') return 'Fondo blanco';
  if (sel.mode === 'biblioteca') return sel.name;
  if (sel.mode === 'guardado') return sel.name;
  return 'Descripción';
};

// ─── Sub-componente: ListView ─────────────────────────────────────────

interface ListViewProps {
  selection: FondoSelection | null;
  biblioteca: FondoBiblioteca[];
  guardados: FondoGuardado[];
  onSelectAuto: () => void;
  onSelectBlanco: () => void;
  onSelectBiblioteca: (f: FondoBiblioteca) => void;
  onSelectGuardado: (f: FondoGuardado) => void;
  onTapDescribe: () => void;
  onTapUpload: () => void;
  variant: 'sheet' | 'inline';
}

const ListView: React.FC<ListViewProps> = ({
  selection,
  biblioteca,
  guardados,
  onSelectAuto,
  onSelectBlanco,
  onSelectBiblioteca,
  onSelectGuardado,
  onTapDescribe,
  onTapUpload,
  variant,
}) => {
  const isSheet = variant === 'sheet';
  const cardClass = isSheet
    ? 'shrink-0 w-[220px] aspect-[9/16]'
    : 'shrink-0 w-[140px] aspect-[9/16]';
  const autoSelected = selection?.mode === 'auto';
  const blancoSelected = selection?.mode === 'blanco';
  const bibliotecaSelectedId =
    selection?.mode === 'biblioteca' ? selection.id : null;
  const guardadoSelectedFile =
    selection?.mode === 'guardado' ? selection.fileName : null;

  // Cards de fondos. `cls` fija la medida (carrusel: fija; grid: fluida),
  // `big` el tamaño de iconos/texto. Devuelve un array para reusar en el
  // carrusel mobile y el grid desktop.
  const renderCards = (cls: string, big: boolean): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    // Automático
    out.push(
      <button
        key="auto"
        type="button"
        onClick={onSelectAuto}
        className={`${cls} rounded-2xl overflow-hidden relative flex flex-col items-center justify-center gap-2.5 text-white shadow-sm transition-transform active:scale-[0.97] bg-brand-primary ${
          autoSelected ? 'ring-2 ring-brand-primary ring-offset-2' : ''
        }`}
      >
        <AnimatedBlurBg pauseOnDesktopUnselected selected={autoSelected} />
        <Sparkles
          className={`relative z-10 ${big ? 'w-9 h-9 drop-shadow' : 'w-7 h-7 drop-shadow'}`}
        />
        <span
          className={`relative z-10 ${big ? 'text-[15px]' : 'text-[13.5px]'} font-semibold leading-tight px-2 text-center drop-shadow`}
        >
          Automático
        </span>
      </button>,
    );
    // Fondos guardados del user
    for (const f of guardados) {
      const isSel = guardadoSelectedFile === f.fileName;
      out.push(
        <button
          key={`g-${f.id}`}
          type="button"
          onClick={() => onSelectGuardado(f)}
          className={`${cls} rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
            isSel ? 'border-brand-primary' : 'border-neutral-200'
          } transition-all relative active:scale-[0.97]`}
        >
          {f.imageUrl && (
            <CachedImg
              src={f.imageUrl}
              alt={f.nombre}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          )}
          {isSel && (
            <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
              <Check className="w-4 h-4" strokeWidth={3} />
            </span>
          )}
          <div
            className={`absolute inset-x-0 bottom-0 ${big ? 'px-3 py-2.5' : 'px-2 py-1.5'} bg-gradient-to-t from-black/65 via-black/15 to-transparent`}
          >
            <p
              className={`text-left text-white ${big ? 'text-[15px]' : 'text-[13px]'} font-semibold leading-tight drop-shadow truncate`}
            >
              {f.nombre}
            </p>
          </div>
        </button>,
      );
    }
    // Fondo blanco — tile estático
    out.push(
      <button
        key="blanco"
        type="button"
        onClick={onSelectBlanco}
        className={`${cls} rounded-2xl overflow-hidden bg-white border-2 ${
          blancoSelected ? 'border-brand-primary' : 'border-neutral-200'
        } transition-all relative active:scale-[0.97] flex flex-col items-center justify-center gap-2`}
      >
        <span
          className={`inline-flex items-center justify-center rounded-md border-2 border-neutral-300 bg-white ${big ? 'w-12 h-12' : 'w-9 h-9'}`}
        />
        <span
          className={`text-brand-black/85 font-semibold leading-tight ${big ? 'text-[15px]' : 'text-[13px]'}`}
        >
          Fondo blanco
        </span>
        {blancoSelected && (
          <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
            <Check className="w-4 h-4" strokeWidth={3} />
          </span>
        )}
      </button>,
    );
    // Biblioteca pública
    for (const f of biblioteca) {
      const isSel = bibliotecaSelectedId === f.id;
      out.push(
        <button
          key={`b-${f.id}`}
          type="button"
          onClick={() => onSelectBiblioteca(f)}
          className={`${cls} rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
            isSel ? 'border-brand-primary' : 'border-neutral-200'
          } transition-all relative active:scale-[0.97]`}
        >
          {f.imageUrl && (
            <CachedImg
              src={f.imageUrl}
              alt={f.nombre}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          )}
          <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-black/55 backdrop-blur-sm text-white text-[9.5px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-md">
            <Sparkles className="w-2.5 h-2.5" />
            Atlas
          </span>
          {isSel && (
            <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
              <Check className="w-4 h-4" strokeWidth={3} />
            </span>
          )}
          <div
            className={`absolute inset-x-0 bottom-0 ${big ? 'px-3 py-2.5' : 'px-2 py-1.5'} bg-gradient-to-t from-black/65 via-black/15 to-transparent`}
          >
            <p
              className={`text-left text-white ${big ? 'text-[15px]' : 'text-[13px]'} font-semibold leading-tight drop-shadow truncate`}
            >
              {f.nombre}
            </p>
          </div>
        </button>,
      );
    }
    return out;
  };

  // Opciones de la card "Agregar fondo" (popover en el grid desktop).
  const addOptions = [
    {
      icon: <TypeIcon className="w-3.5 h-3.5" />,
      label: 'Describir tu fondo',
      onClick: onTapDescribe,
    },
    {
      icon: <UserPlus className="w-3.5 h-3.5" />,
      label: 'Cargá tu propio fondo',
      onClick: onTapUpload,
    },
  ];

  const carousel = (
    <HoverScrollCarousel
      className="flex gap-3 overflow-x-auto -mx-5 px-5 scrollbar-hide"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
      arrowInset={4}
    >
      {renderCards(cardClass, isSheet)}
    </HoverScrollCarousel>
  );

  // Grid desktop del sheet — 2 columnas + card "Agregar fondo".
  const desktopGrid = (
    <div className="h-full overflow-y-auto -mx-1 px-1 pb-1">
      <div className="grid grid-cols-2 gap-3">
        {(() => {
          const cards = renderCards('w-full aspect-[9/16]', true);
          return (
            <>
              {cards[0]}
              <AddOptionCard
                key="add-fondo"
                label="Agregar fondo"
                className="w-full aspect-[9/16]"
                options={addOptions}
              />
              {cards.slice(1)}
            </>
          );
        })()}
      </div>
    </div>
  );

  const ctaButtons = (
    <div className="flex flex-col gap-2.5">
      <button
        type="button"
        onClick={onTapDescribe}
        className="w-full inline-flex items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[14.5px] font-medium transition-colors"
      >
        <span className="inline-flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
            <TypeIcon className="w-3.5 h-3.5 text-brand-primary" />
          </span>
          Describir tu fondo
        </span>
        <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
      </button>
      <button
        type="button"
        onClick={onTapUpload}
        className="w-full inline-flex items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[14.5px] font-medium transition-colors"
      >
        <span className="inline-flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
            <UserPlus className="w-3.5 h-3.5 text-brand-primary" />
          </span>
          Cargá tu propio fondo
        </span>
        <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
      </button>
    </div>
  );

  if (isSheet) {
    // Mobile: carrusel horizontal + CTAs abajo. Desktop: grid 2 columnas
    // con card "Agregar fondo" (popover describir / subir).
    return (
      <div className="flex flex-col h-full">
        <div className="md:hidden flex flex-col h-full">
          <div className="flex-1 flex items-center min-h-0">{carousel}</div>
          <div className="shrink-0 pt-4">{ctaButtons}</div>
        </div>
        <div className="hidden md:block h-full min-h-0">{desktopGrid}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {carousel}
      {ctaButtons}
    </div>
  );
};

// ─── Describe view ────────────────────────────────────────────────────

// ─── Chat message row (mobile only) ──────────────────────────────────

const ChatMessageRow: React.FC<{
  msg: DescribeChatMessage;
  onUse: (prompt: string) => void;
}> = ({ msg, onUse }) => {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end animate-mode-swap">
        <div className="max-w-[80%] bg-[#F2F2F7] rounded-[18px] rounded-tr-[6px] px-4 py-2.5 text-[14.5px] text-brand-black/90 leading-snug whitespace-pre-wrap">
          {msg.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2.5 animate-mode-swap">
      {msg.text && (
        <p className="text-[14.5px] text-brand-black/85 leading-relaxed whitespace-pre-wrap">
          {msg.text}
        </p>
      )}
      {msg.suggestions?.map((s, i) => (
        <div
          key={i}
          className="rounded-2xl border border-neutral-200 bg-white p-3.5 flex flex-col gap-2.5"
        >
          {s.titulo && (
            <p className="text-[13px] font-semibold text-brand-black/85 leading-tight">
              {s.titulo}
            </p>
          )}
          <p className="text-[14px] text-brand-black/80 leading-relaxed whitespace-pre-wrap">
            {s.prompt}
          </p>
          <button
            type="button"
            onClick={() => onUse(s.prompt)}
            className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-semibold shadow-sm active:scale-[0.97] transition-transform"
          >
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
            Utilizar
          </button>
        </div>
      ))}
    </div>
  );
};

interface DescribeViewProps {
  initial?: string;
  onBack: () => void;
  onConfirm: (text: string) => void;
  /** Si está provisto, el componente reporta cada cambio al padre. El
   *  padre puede usarlo para habilitar/disparar un botón "Usar" en el
   *  header (mobile sheet) en vez del CTA inline. */
  onTextChange?: (text: string) => void;
  /** Nombres de los productos seleccionados — el chat AI los usa como
   *  contexto en el system prompt. */
  selectedProductNames?: string[];
  /** Rubro de la empresa — también va al system prompt. */
  rubro?: string | null;
  /** Estado del chat mode (mobile only). Lifteado al sheet para que
   *  el header pueda ocultar el botón "Usar" mientras está chateando. */
  chatMode?: boolean;
  onChatModeChange?: (b: boolean) => void;
}

const DescribeView: React.FC<DescribeViewProps> = ({
  initial,
  onBack,
  onConfirm,
  onTextChange,
  selectedProductNames = [],
  rubro = null,
  chatMode = false,
  onChatModeChange,
}) => {
  const [text, setText] = useState<string>(initial || '');
  const submit = () => onConfirm(text.trim());
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mobileTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  // Auto-grow del textarea: arranca con altura para 3 filas y crece
  // hasta 15 antes de scrollear. Calculamos el cap leyendo line-height
  // y padding del propio elemento (robusto a cambios de tipografía).
  const adjustTextareaHeight = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    const styles = window.getComputedStyle(el);
    const lineHeight =
      parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.4;
    const padY =
      (parseFloat(styles.paddingTop) || 0) +
      (parseFloat(styles.paddingBottom) || 0);
    const borderY =
      (parseFloat(styles.borderTopWidth) || 0) +
      (parseFloat(styles.borderBottomWidth) || 0);
    const maxHeight = lineHeight * 15 + padY + borderY;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };
  const handleChange = (v: string) => {
    setText(v);
    onTextChange?.(v);
  };
  // Re-ajustar el alto cuando cambia el texto. Aplica a ambos textareas
  // (solo uno está visible por viewport, pero ambos viven en el DOM).
  useEffect(() => {
    adjustTextareaHeight(mobileTextareaRef.current);
    adjustTextareaHeight(textareaRef.current);
  }, [text]);
  // Inicial: ajustamos en mount y cada vez que salimos de chatMode (el
  // textarea reaparece y puede haber cambiado de tamaño contenedor).
  useEffect(() => {
    const t = setTimeout(() => {
      adjustTextareaHeight(mobileTextareaRef.current);
      adjustTextareaHeight(textareaRef.current);
    }, 0);
    return () => clearTimeout(t);
  }, [chatMode]);
  // Auto-focus al entrar — el user llega a esta view explícitamente
  // para escribir. Ambos textareas (mobile + desktop) están en el DOM;
  // elegimos el visible vía matchMedia. Pequeño delay para no pelear
  // con la animación de entrada del sheet.
  useEffect(() => {
    if (chatMode) return; // en chatMode el textarea de arriba ya no está visible
    const t = setTimeout(() => {
      const isDesktop =
        typeof window !== 'undefined' &&
        window.matchMedia('(min-width: 768px)').matches;
      const el = isDesktop ? textareaRef.current : mobileTextareaRef.current;
      el?.focus();
    }, 80);
    return () => clearTimeout(t);
  }, [chatMode]);

  // ─── Chat (mobile only) ──────────────────────────────────────────────
  const chat = useDescribeChat({
    productNames: selectedProductNames,
    rubro,
  });
  const [chatInput, setChatInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }, [chat.messages, chat.streaming]);

  const startChat = async () => {
    if (chat.streaming) return;
    const hasText = text.trim().length > 0;
    const initialPrompt = hasText
      ? `Ayudame a mejorar esta idea de fondo: "${text.trim()}"`
      : 'Sugerime ideas de fondos.';
    onChatModeChange?.(true);
    await chat.start(initialPrompt);
  };

  const handleSendFollowUp = async () => {
    const msg = chatInput.trim();
    if (!msg) return;
    setChatInput('');
    await chat.sendFollowUp(msg);
  };

  const aiButtonDisabled = chat.streaming;
  const aiButtonLabel = text.trim()
    ? 'Ayudame a mejorar mi idea'
    : 'Sugerime ideas';

  return (
    <>
      {/* ── Mobile: textarea inicial + botón AI. Al tocar el botón,
          entra en chatMode: messages flex-1 arriba, input al bottom. ── */}
      <div className="md:hidden h-full flex flex-col">
        {/* Vista inicial — colapsa cuando entra chatMode */}
        <div
          className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
            chatMode ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
          }`}
        >
          <div className="min-h-0">
            <textarea
              ref={mobileTextareaRef}
              value={text}
              onChange={(e) => handleChange(e.target.value)}
              placeholder="Ej: estudio minimalista con luz natural lateral, fondo gris suave…"
              rows={3}
              className="w-full resize-none rounded-2xl border border-neutral-200 bg-white px-4 py-3.5 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
            />
            <button
              type="button"
              onClick={startChat}
              disabled={aiButtonDisabled}
              className="relative mt-3 w-full overflow-hidden rounded-2xl py-3 px-4 text-[14.5px] font-semibold text-white shadow-sm bg-white border border-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
            >
              {/* Blurs subtítulos — anclados al bottom, asomando hacia
                  abajo. Misma idea que el AutoPlaceholder de las cards
                  miniatura Modelo/Pose: opacidades bajas para que el
                  fondo del botón siga siendo blanco. */}
              <span className="pointer-events-none absolute inset-0">
                <span
                  className="absolute rounded-full blur-2xl animate-drift-1"
                  style={{
                    width: '50%',
                    height: '180%',
                    backgroundColor: '#1644EB',
                    left: '-5%',
                    top: '-40%',
                    opacity: 0.9,
                  }}
                />
                <span
                  className="absolute rounded-full blur-2xl animate-drift-2"
                  style={{
                    width: '45%',
                    height: '170%',
                    backgroundColor: '#06B6D4',
                    right: '0%',
                    top: '-50%',
                    opacity: 0.8,
                  }}
                />
                <span
                  className="absolute rounded-full blur-2xl animate-drift-3"
                  style={{
                    width: '55%',
                    height: '180%',
                    backgroundColor: '#EC4899',
                    left: '30%',
                    top: '-40%',
                    opacity: 0.85,
                  }}
                />
              </span>
              <span className="relative z-10 inline-flex items-center justify-center gap-2 drop-shadow">
                <Sparkles className="w-4 h-4 text-white" />
                {aiButtonLabel}
              </span>
            </button>
          </div>
        </div>
        {/* Chat — aparece cuando chatMode es true. Crece de 0 a 1fr con
            transición sobre grid-template-rows para que se sienta como el
            input bajando. */}
        <div
          className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
            chatMode ? 'grid-rows-[1fr] flex-1' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="min-h-0 flex flex-col">
            <div className="flex-1 overflow-y-auto -mx-5 px-5">
              <div className="flex flex-col gap-3 py-1">
                {chat.messages.map((m, i) => {
                  const isLast = i === chat.messages.length - 1;
                  const showDots =
                    chat.streaming &&
                    isLast &&
                    m.role === 'assistant' &&
                    !m.text &&
                    !m.suggestions;
                  return (
                    <React.Fragment key={i}>
                      {showDots ? (
                        <div className="flex items-center gap-1.5 py-1">
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '0ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '150ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '300ms' }}
                          />
                        </div>
                      ) : (
                        <ChatMessageRow
                          msg={m}
                          onUse={(prompt) => onConfirm(prompt)}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>
        </div>
        {/* Input al bottom — visible solo en chatMode. */}
        {chatMode && (
          <div className="pt-3 shrink-0">
            <div className="bg-white rounded-2xl shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04] focus-within:ring-brand-primary/25 transition-[box-shadow,--tw-ring-color] duration-200">
              <textarea
                ref={chatInputRef}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendFollowUp();
                  }
                }}
                placeholder="Afiná tu idea con Atlas…"
                rows={1}
                disabled={chat.streaming}
                className="w-full resize-none bg-transparent outline-none px-4 pt-4 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 leading-snug"
              />
              <div className="flex items-center justify-end gap-2 px-3 pb-2.5 pt-2">
                <button
                  type="button"
                  onClick={handleSendFollowUp}
                  disabled={chatInput.trim().length === 0 || chat.streaming}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                    chatInput.trim()
                      ? 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                      : 'bg-neutral-100 text-brand-black/70'
                  }`}
                  aria-label="Enviar"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Desktop: mismo flujo que mobile (textarea + botón AI →
          chatMode con messages y chat input). Cancelar/Aceptar footer
          solo se muestra fuera de chatMode. ── */}
      <div className="hidden md:flex md:flex-col md:min-h-full md:h-full">
        {/* Vista inicial — colapsa con grid-template-rows transición. */}
        <div
          className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
            chatMode ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
          }`}
        >
          <div className="min-h-0">
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => handleChange(e.target.value)}
                placeholder="Describí la escena que querés para tus imágenes"
                rows={5}
                className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent"
              />
            </div>
            <button
              type="button"
              onClick={startChat}
              disabled={aiButtonDisabled}
              className="relative mt-3 w-full overflow-hidden rounded-2xl py-3 px-4 text-[14.5px] font-semibold text-white shadow-sm bg-white border border-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
            >
              <span className="pointer-events-none absolute inset-0">
                <span
                  className="absolute rounded-full blur-2xl animate-drift-1"
                  style={{
                    width: '50%',
                    height: '180%',
                    backgroundColor: '#1644EB',
                    left: '-5%',
                    top: '-40%',
                    opacity: 0.9,
                  }}
                />
                <span
                  className="absolute rounded-full blur-2xl animate-drift-2"
                  style={{
                    width: '45%',
                    height: '170%',
                    backgroundColor: '#06B6D4',
                    right: '0%',
                    top: '-50%',
                    opacity: 0.8,
                  }}
                />
                <span
                  className="absolute rounded-full blur-2xl animate-drift-3"
                  style={{
                    width: '55%',
                    height: '180%',
                    backgroundColor: '#EC4899',
                    left: '30%',
                    top: '-40%',
                    opacity: 0.85,
                  }}
                />
              </span>
              <span className="relative z-10 inline-flex items-center justify-center gap-2 drop-shadow">
                <Sparkles className="w-4 h-4 text-white" />
                {aiButtonLabel}
              </span>
            </button>
          </div>
        </div>
        {/* Chat — aparece cuando chatMode es true. */}
        <div
          className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
            chatMode ? 'grid-rows-[1fr] flex-1' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="min-h-0 flex flex-col">
            <div className="flex-1 overflow-y-auto -mx-5 px-5">
              <div className="flex flex-col gap-3 py-1">
                {chat.messages.map((m, i) => {
                  const isLast = i === chat.messages.length - 1;
                  const showDots =
                    chat.streaming &&
                    isLast &&
                    m.role === 'assistant' &&
                    !m.text &&
                    !m.suggestions;
                  return (
                    <React.Fragment key={i}>
                      {showDots ? (
                        <div className="flex items-center gap-1.5 py-1">
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '0ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '150ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce"
                            style={{ animationDelay: '300ms' }}
                          />
                        </div>
                      ) : (
                        <ChatMessageRow
                          msg={m}
                          onUse={(prompt) => onConfirm(prompt)}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {/* Input chat al bottom — visible solo en chatMode. `pb-4` da
            aire abajo en desktop: el scroll wrapper del sheet usa
            md:pb-0 y el shadow del input se cortaba contra el borde. */}
        {chatMode && (
          <div className="pt-3 pb-4 shrink-0">
            <div className="bg-white rounded-2xl shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04] focus-within:ring-brand-primary/25 transition-[box-shadow,--tw-ring-color] duration-200">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendFollowUp();
                  }
                }}
                placeholder="Afiná tu idea con Atlas…"
                rows={1}
                disabled={chat.streaming}
                className="w-full resize-none bg-transparent outline-none px-4 pt-4 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 leading-snug"
              />
              <div className="flex items-center justify-end gap-2 px-3 pb-2.5 pt-2">
                <button
                  type="button"
                  onClick={handleSendFollowUp}
                  disabled={chatInput.trim().length === 0 || chat.streaming}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                    chatInput.trim()
                      ? 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                      : 'bg-neutral-100 text-brand-black/70'
                  }`}
                  aria-label="Enviar"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Footer Cancelar/Aceptar — solo fuera de chatMode. */}
        {!chatMode && (
          <div className="sticky bottom-0 mt-auto -mx-5 px-5 pt-3 pb-3 bg-white flex justify-end gap-3 border-t border-black/[0.06]">
            <button
              type="button"
              onClick={onBack}
              className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={text.trim().length === 0}
              className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
                text.trim()
                  ? 'text-white bg-brand-primary hover:bg-brand-primary-hover'
                  : 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
              }`}
            >
              Utilizar
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Upload view ──────────────────────────────────────────────────────

interface UploadViewProps {
  companyId: string | null;
  onConfirm: (saved: FondoGuardado) => void;
  onFondoSaved: () => void;
}

const UploadView: React.FC<UploadViewProps> = ({
  companyId,
  onConfirm,
  onFondoSaved,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleConfirm = async () => {
    if (!file) return;
    if (!companyId) {
      setError('No pudimos identificar la empresa.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const saved = await uploadFondo({ companyId, file, name });
      onFondoSaved();
      onConfirm(saved);
    } catch (err) {
      console.error('[FondoPicker] uploadFondo error', err);
      setError(
        err instanceof Error
          ? err.message
          : 'No pudimos subir el fondo. Probá de nuevo.',
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 md:min-h-full">
      {/* Intro — solo mobile (en desktop el header del sheet ya tiene el
          título y el padding interno alcanza). */}
      <div className="md:hidden">
        <p className="text-[15.5px] font-semibold text-brand-black/90">
          Subí tu fondo
        </p>
        <p className="text-[13px] text-[#3C3C43]/60 mt-0.5 leading-relaxed">
          Una imagen del lugar / escenario que querés usar. Queda guardada
          en tu biblioteca para reusarla después.
        </p>
      </div>
      <p className="hidden md:block text-[15px] text-neutral-500">
        Una imagen del lugar / escenario que querés usar. Queda guardada en
        tu biblioteca para reusarla después.
      </p>
      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={converting}
          className="w-full inline-flex flex-col items-center justify-center gap-2 py-10 rounded-2xl border-2 border-dashed border-neutral-300 hover:border-brand-primary/40 bg-white text-[#3C3C43]/55 hover:text-brand-primary transition-colors disabled:opacity-60 disabled:cursor-wait"
        >
          <Upload className="w-6 h-6" />
          <span className="text-[14px] font-medium">
            {converting ? 'Convirtiendo…' : 'Subir imagen'}
          </span>
        </button>
      ) : (
        <div className="relative rounded-2xl overflow-hidden bg-neutral-100 flex items-center justify-center max-h-[42dvh]">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Fondo"
              className="block max-w-full max-h-[42dvh] w-auto h-auto object-contain"
            />
          )}
          <button
            type="button"
            onClick={() => setFile(null)}
            aria-label="Quitar"
            className="absolute top-2 right-2 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          setError(null);
          // Convertir HEIC/HEIF/AVIF→JPEG acá también (no solo en uploadFondo)
          // porque el preview es un <img> del archivo crudo y el navegador no
          // renderiza HEIC: sin esto el preview quedaba roto.
          try {
            setConverting(true);
            setFile(await normalizeImageForUpload(f));
          } catch (convErr) {
            console.error('[FondoPicker] conversión de formato falló', convErr);
            setError('No pudimos procesar esa imagen. Probá con otra (JPG o PNG).');
          } finally {
            setConverting(false);
          }
        }}
      />
      {file && (
        <div>
          <label className="block text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
            Nombre del fondo
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Opcional"
            disabled={uploading}
            className="w-full h-11 px-4 rounded-xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary disabled:opacity-50 transition-colors"
          />
        </div>
      )}
      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
          {error}
        </div>
      )}
      {file && (
        <>
          {/* Mobile: botón pill full-width "Utilizar". */}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={uploading}
            className="md:hidden w-full inline-flex items-center justify-center py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
          >
            {uploading ? 'Subiendo…' : 'Utilizar'}
          </button>
          {/* Desktop: footer al bottom (mt-auto + sticky). */}
          <div className="hidden md:flex sticky bottom-0 mt-auto -mx-5 px-5 pt-3 pb-3 bg-white justify-end gap-3 border-t border-black/[0.06]">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={uploading}
              className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
                uploading
                  ? 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
                  : 'text-white bg-brand-primary hover:bg-brand-primary-hover'
              }`}
            >
              {uploading ? 'Subiendo…' : 'Aceptar'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Inline (desktop) ─────────────────────────────────────────────────

interface FondoPickerInlineProps {
  selection: FondoSelection | null;
  onChange: (sel: FondoSelection) => void;
  view: FondoPickerView;
  onViewChange: (v: FondoPickerView) => void;
  biblioteca: FondoBiblioteca[];
  guardados: FondoGuardado[];
  companyId: string | null;
  onFondoSaved: () => void;
  /** Si true, oculta el label "Fondo" interno (lo provee el caller). */
  hideLabel?: boolean;
  /** Opcional: si está provisto, los botones "Describí el fondo" / "Cargá
   *  tu fondo" llaman a este callback en vez de cambiar el view inline.
   *  Lo usa design-modelo en desktop para abrir el sheet como side panel. */
  onOpenSheet?: (initialView: FondoPickerView) => void;
  /** Nombres de productos seleccionados — contexto para el chat AI. */
  selectedProductNames?: string[];
  /** Rubro de la empresa — contexto para el chat AI. */
  rubro?: string | null;
}

export const FondoPickerInline: React.FC<FondoPickerInlineProps> = ({
  selection,
  onChange,
  view,
  onViewChange,
  biblioteca,
  guardados,
  companyId,
  onFondoSaved,
  hideLabel = false,
  onOpenSheet,
  selectedProductNames = [],
  rubro = null,
}) => {
  // Si view !== 'list' siempre mostramos "Volver" — sin label aunque
  // hideLabel sea true, ese botón es de navegación dentro del picker.
  // En 'list' + hideLabel + onOpenSheet provisto, mostramos "Ver más" a
  // la derecha (mismo patrón que PosePickerInline) que abre el sheet en
  // vista 'all' (grid completa de todos los fondos).
  const showVerMas = hideLabel && view === 'list' && !!onOpenSheet;
  const showHeader = !hideLabel || view !== 'list' || showVerMas;
  return (
    <div className="flex flex-col gap-3">
      {showHeader && (
        <div
          className={`flex items-center ${hideLabel ? 'justify-end' : 'justify-between'}`}
        >
          {!hideLabel && (
            <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
              Fondo
            </p>
          )}
          {view !== 'list' && (
            <button
              type="button"
              onClick={() => onViewChange('list')}
              className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Volver
            </button>
          )}
          {showVerMas && (
            <button
              type="button"
              onClick={() => onOpenSheet && onOpenSheet('all')}
              className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
            >
              Ver más
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      {view === 'list' && (
        <ListView
          selection={selection}
          biblioteca={biblioteca}
          guardados={guardados}
          variant="inline"
          onSelectAuto={() => onChange({ mode: 'auto' })}
          onSelectBlanco={() => onChange({ mode: 'blanco' })}
          onSelectBiblioteca={(f) =>
            onChange({
              mode: 'biblioteca',
              id: f.id,
              name: f.nombre,
              imageUrl: f.imageUrl,
            })
          }
          onSelectGuardado={(f) =>
            onChange({
              mode: 'guardado',
              fileName: f.fileName,
              name: f.nombre,
              imageUrl: f.imageUrl,
            })
          }
          onTapDescribe={() =>
            onOpenSheet ? onOpenSheet('describe') : onViewChange('describe')
          }
          onTapUpload={() =>
            onOpenSheet ? onOpenSheet('upload') : onViewChange('upload')
          }
        />
      )}
      {view === 'describe' && (
        <DescribeView
          initial={selection?.mode === 'descripcion' ? selection.text : ''}
          onBack={() => onViewChange('list')}
          onConfirm={(text) => {
            onChange({ mode: 'descripcion', text });
            onViewChange('list');
          }}
          selectedProductNames={selectedProductNames}
          rubro={rubro}
        />
      )}
      {view === 'upload' && (
        <UploadView
          companyId={companyId}
          onFondoSaved={onFondoSaved}
          onConfirm={(saved) => {
            onChange({
              mode: 'guardado',
              fileName: saved.fileName,
              name: saved.nombre,
              imageUrl: saved.imageUrl,
            });
            onViewChange('list');
          }}
        />
      )}
    </div>
  );
};

// ─── AllFondosView ────────────────────────────────────────────────────
// Vista "Ver más" — grilla de TODOS los fondos (guardados del user +
// biblioteca pública) filtrable por categoría. Replica el menú
// "Biblioteca de escenas" de PruebaNuevaInterfaz: tabs con categorías
// arriba, grid 3-col abajo, footer sticky con Cancelar/Aceptar.
// Selección es DRAFT: se commitea sólo al tocar Aceptar.

interface AllFondosViewProps {
  selection: FondoSelection | null;
  biblioteca: FondoBiblioteca[];
  guardados: FondoGuardado[];
  onConfirm: (sel: FondoSelection) => void;
  onCancel: () => void;
}

const AllFondosView: React.FC<AllFondosViewProps> = ({
  selection,
  biblioteca,
  guardados,
  onConfirm,
  onCancel,
}) => {
  // Categorías = "Todos" + "Mis fondos" (si hay guardados) +
  // categorías únicas de biblioteca. Default: 'todos'.
  const bibliotecaCategorias = React.useMemo(() => {
    const set = new Set<string>();
    biblioteca.forEach((f) => {
      if (f.categoria) set.add(f.categoria);
    });
    return Array.from(set).sort();
  }, [biblioteca]);
  const [activeTab, setActiveTab] = useState<string>('todos');
  // Draft de selección — se commitea con Aceptar.
  const [draft, setDraft] = useState<FondoSelection | null>(selection);

  // Items filtrados según la tab activa.
  type Item =
    | { kind: 'biblioteca'; f: FondoBiblioteca }
    | { kind: 'guardado'; f: FondoGuardado };
  const items: Item[] = React.useMemo(() => {
    if (activeTab === 'todos') {
      return [
        ...guardados.map((f): Item => ({ kind: 'guardado', f })),
        ...biblioteca.map((f): Item => ({ kind: 'biblioteca', f })),
      ];
    }
    if (activeTab === 'mis-fondos') {
      return guardados.map((f): Item => ({ kind: 'guardado', f }));
    }
    return biblioteca
      .filter((f) => f.categoria === activeTab)
      .map((f): Item => ({ kind: 'biblioteca', f }));
  }, [activeTab, biblioteca, guardados]);

  const isItemSelected = (it: Item): boolean => {
    if (!draft) return false;
    if (it.kind === 'biblioteca')
      return draft.mode === 'biblioteca' && draft.id === it.f.id;
    return draft.mode === 'guardado' && draft.fileName === it.f.fileName;
  };
  const selectItem = (it: Item) => {
    if (it.kind === 'biblioteca') {
      setDraft({
        mode: 'biblioteca',
        id: it.f.id,
        name: it.f.nombre,
        imageUrl: it.f.imageUrl,
      });
    } else {
      setDraft({
        mode: 'guardado',
        fileName: it.f.fileName,
        name: it.f.nombre,
        imageUrl: it.f.imageUrl,
      });
    }
  };

  const tabs: { id: string; label: string }[] = [
    { id: 'todos', label: 'Todos' },
    ...(guardados.length > 0
      ? [{ id: 'mis-fondos', label: 'Mis fondos' }]
      : []),
    ...bibliotecaCategorias.map((c) => ({ id: c, label: c })),
  ];

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 overflow-y-auto px-4 md:px-5 pt-2 md:pt-4 pb-4">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-3 md:mb-4">
          {tabs.map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`shrink-0 inline-flex items-center px-3.5 md:px-4 h-9 rounded-full border text-[13px] md:text-[15px] font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'bg-white text-brand-black/90 border-neutral-200 hover:bg-neutral-50'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {/* Grilla */}
        {items.length === 0 ? (
          <p className="text-[13.5px] text-[#3C3C43]/55 mt-6 text-center">
            No hay fondos en esta categoría.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-3">
            {items.map((it) => {
              const isSel = isItemSelected(it);
              return (
                <button
                  key={`${it.kind}-${it.f.id}`}
                  type="button"
                  onClick={() => selectItem(it)}
                  className={`relative rounded-md md:rounded-[13px] overflow-hidden bg-white border p-2 text-left transition-colors ${
                    isSel
                      ? 'border-brand-primary ring-2 ring-brand-primary'
                      : 'border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  <div className="relative overflow-hidden rounded-md h-24 md:h-28 bg-neutral-100">
                    {it.f.imageUrl && (
                      <CachedImg
                        src={it.f.imageUrl}
                        alt={it.f.nombre}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    )}
                    {isSel && (
                      <span className="absolute top-2 right-2 w-5 h-5 rounded-full inline-flex items-center justify-center bg-brand-primary text-white">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-neutral-700 mt-2 truncate">
                    {it.f.nombre}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* Footer sticky — Cancelar / Aceptar. */}
      <div className="shrink-0 px-5 py-3 flex justify-end gap-3 border-t border-black/[0.06] bg-white">
        <button
          type="button"
          onClick={onCancel}
          className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => draft && onConfirm(draft)}
          disabled={!draft}
          className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
            draft
              ? 'text-white bg-brand-primary hover:bg-brand-primary-hover'
              : 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
          }`}
        >
          Aceptar
        </button>
      </div>
    </div>
  );
};

// ─── Sheet (mobile) ───────────────────────────────────────────────────

interface FondoPickerSheetProps {
  open: boolean;
  selection: FondoSelection | null;
  onChange: (sel: FondoSelection) => void;
  onClose: () => void;
  biblioteca: FondoBiblioteca[];
  guardados: FondoGuardado[];
  companyId: string | null;
  onFondoSaved: () => void;
  /** Vista inicial al abrir — útil para entrar directo a 'describe' o
   *  'upload' desde el inline picker en desktop. Default 'list'. */
  initialView?: FondoPickerView;
  /** Nombres de los productos seleccionados — para el chat AI de
   *  "Describí el fondo" (mejorar idea / sugerir ideas). */
  selectedProductNames?: string[];
  /** Rubro de la empresa — también pasa al system prompt del agente. */
  rubro?: string | null;
}

export const FondoPickerSheet: React.FC<FondoPickerSheetProps> = ({
  open,
  selection,
  onChange,
  onClose,
  biblioteca,
  guardados,
  companyId,
  onFondoSaved,
  initialView = 'list',
  selectedProductNames = [],
  rubro = null,
}) => {
  const [view, setView] = useState<FondoPickerView>(initialView);
  // `useExitTransition` mantiene el sheet montado durante la animación
  // de salida cuando `open` pasa a false (al elegir un fondo o tocar
  // Cancelar / overlay) — así el sheet se desliza inverso antes de
  // desmontarse.
  const { mounted, animated } = useExitTransition(open, 320);
  // Texto del DescribeView lifteado para que el header (mobile) pueda
  // mostrar/disablar el botón "Usar" en su slot derecho.
  const [describeText, setDescribeText] = useState<string>(
    selection?.mode === 'descripcion' ? selection.text : '',
  );
  // Mobile chat-mode dentro de DescribeView. Cuando true, ocultamos el
  // "Usar" del header (el user usa "Utilizar" en cada sugerencia).
  const [describeChatMode, setDescribeChatMode] = useState(false);
  useEffect(() => {
    if (open) {
      setView(initialView);
      setDescribeText(
        selection?.mode === 'descripcion' ? selection.text : '',
      );
      setDescribeChatMode(false);
      return;
    }
    // Reset interno del view con delay para no ver el wipe durante la
    // animación de salida.
    const t = setTimeout(() => setView('list'), 320);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Salir del chat-mode si el user cambia de view (volver a list, etc).
  useEffect(() => {
    if (view !== 'describe') setDescribeChatMode(false);
  }, [view]);

  if (!mounted) return null;

  const titleByView: Record<FondoPickerView, string> = {
    list: 'Fondo',
    describe: 'Describí el fondo',
    upload: 'Cargá tu fondo',
    all: 'Biblioteca de fondos',
  };

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[630px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          // En mobile la altura del sheet (en className) ya excluye el
          // safe area top — bottom-anclado sin zona blanca arriba.
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Mobile: iOS-style header. */}
        <div className="md:hidden flex items-center justify-between px-4 pb-3 shrink-0">
          {view === 'list' ? (
            <button
              type="button"
              onClick={onClose}
              className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
            >
              Cancelar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setView('list')}
              className="inline-flex items-center gap-0.5 text-brand-primary text-[15px] active:opacity-50 transition-opacity w-20 text-left"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          )}
          <h3 className="text-[15px] font-semibold text-brand-black/90 truncate max-w-[55%] text-center">
            {titleByView[view]}
          </h3>
          <div className="w-20 flex justify-end">
            {view === 'list' && (
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {view === 'describe' && !describeChatMode && (
              <button
                type="button"
                onClick={() => {
                  const trimmed = describeText.trim();
                  if (!trimmed) return;
                  onChange({ mode: 'descripcion', text: trimmed });
                  onClose();
                }}
                disabled={describeText.trim().length === 0}
                className="text-[15px] font-semibold text-brand-primary active:opacity-50 transition-opacity disabled:text-brand-primary/30 disabled:cursor-not-allowed"
              >
                Utilizar
              </button>
            )}
          </div>
        </div>
        {/* Desktop: PruebaNuevaInterfaz-style (title left · X right · divider). */}
        <div className="hidden md:flex items-start justify-between px-5 py-4 shrink-0">
          <div className="min-w-0">
            <h3 className="text-[17px] font-semibold text-brand-black/90">
              {titleByView[view]}
            </h3>
            {view === 'list' && (
              <p className="text-[13px] text-[#3C3C43]/55 mt-0.5">
                Elegí el fondo o escenario para todas las imágenes del
                catálogo.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {view === 'list' ? (
          <div className="flex-1 min-h-0 px-5 pt-2 pb-6">
            <ListView
              selection={selection}
              biblioteca={biblioteca}
              guardados={guardados}
              variant="sheet"
              onSelectAuto={() => {
                onChange({ mode: 'auto' });
                onClose();
              }}
              onSelectBlanco={() => {
                onChange({ mode: 'blanco' });
                onClose();
              }}
              onSelectBiblioteca={(f) => {
                onChange({
                  mode: 'biblioteca',
                  id: f.id,
                  name: f.nombre,
                  imageUrl: f.imageUrl,
                });
                onClose();
              }}
              onSelectGuardado={(f) => {
                onChange({
                  mode: 'guardado',
                  fileName: f.fileName,
                  name: f.nombre,
                  imageUrl: f.imageUrl,
                });
                onClose();
              }}
              onTapDescribe={() => setView('describe')}
              onTapUpload={() => setView('upload')}
            />
          </div>
        ) : view === 'all' ? (
          <AllFondosView
            selection={selection}
            biblioteca={biblioteca}
            guardados={guardados}
            onConfirm={(sel) => {
              onChange(sel);
              onClose();
            }}
            onCancel={onClose}
          />
        ) : (
          <div className="flex-1 overflow-y-auto px-5 pt-2 pb-6 md:pt-4 md:pb-0">
            {view === 'describe' && (
              <DescribeView
                initial={
                  selection?.mode === 'descripcion' ? selection.text : ''
                }
                onBack={onClose}
                onConfirm={(text) => {
                  onChange({ mode: 'descripcion', text });
                  onClose();
                }}
                onTextChange={setDescribeText}
                selectedProductNames={selectedProductNames}
                rubro={rubro}
                chatMode={describeChatMode}
                onChatModeChange={setDescribeChatMode}
              />
            )}
            {view === 'upload' && (
              <UploadView
                companyId={companyId}
                onFondoSaved={onFondoSaved}
                onConfirm={(saved) => {
                  onChange({
                    mode: 'guardado',
                    fileName: saved.fileName,
                    name: saved.nombre,
                    imageUrl: saved.imageUrl,
                  });
                  onClose();
                }}
              />
            )}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};
